import React, { useCallback, useEffect, useRef, useState } from "react";
import { getChoiceSlotsForQuestion } from "./quizStageUtils";

const CARD_THEMES = [
  { accent: "#7b3fa8", soft: "#f3e8ff" },
  { accent: "#d94f8c", soft: "#fce8f2" },
  { accent: "#e8892a", soft: "#fff0e0" },
  { accent: "#4aa8d8", soft: "#e5f5fc" },
  { accent: "#3db89a", soft: "#e5f8f3" },
];

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"
      />
    </svg>
  );
}

function Waveform() {
  const bars = [6, 12, 18, 10, 16, 8, 14, 20, 11, 17, 9, 15, 7, 13, 19, 10, 14, 8];
  return (
    <svg
      className="quiz-audio-card__wave"
      viewBox="0 0 120 28"
      width="100%"
      height="28"
      aria-hidden
      preserveAspectRatio="none"
    >
      {bars.map((h, i) => (
        <rect
          key={i}
          x={i * 6.6 + 1}
          y={(28 - h) / 2}
          width="3.2"
          height={h}
          rx="1.2"
          fill="currentColor"
          opacity={0.55 + (i % 3) * 0.12}
        />
      ))}
    </svg>
  );
}

export default function QuizAudioCard({
  question,
  index,
  selectedKey,
  disabled,
  onSelect,
  textStyle,
}) {
  const theme = CARD_THEMES[index % CARD_THEMES.length];
  const choices = getChoiceSlotsForQuestion(question);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const syncProgress = useCallback(() => {
    const el = audioRef.current;
    if (!el || !el.duration) {
      setProgress(0);
      return;
    }
    setProgress((el.currentTime / el.duration) * 100);
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
    };
    const onTime = () => syncProgress();

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("timeupdate", onTime);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("timeupdate", onTime);
    };
  }, [syncProgress, question.audio]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      document.querySelectorAll(".quiz-audio-card__audio").forEach((node) => {
        if (node !== el) node.pause();
      });
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  const seekBy = (delta) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    el.currentTime = Math.max(0, Math.min(el.duration, el.currentTime + delta));
    syncProgress();
  };

  const seekTo = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * el.duration;
    syncProgress();
  };

  return (
    <article
      className="quiz-audio-card"
      style={{
        "--qac-accent": theme.accent,
        "--qac-soft": theme.soft,
      }}
    >
      <div className="quiz-audio-card__player-panel">
        <div className="quiz-audio-card__mic" aria-hidden>
          <MicIcon />
        </div>
        <Waveform />

        {question.audio ? (
          <>
            <audio
              ref={audioRef}
              className="quiz-audio-card__audio"
              src={question.audio}
              preload="metadata"
            />
            <button
              type="button"
              className="quiz-audio-card__seek"
              aria-label={`Seek audio for question ${index + 1}`}
              onClick={seekTo}
            >
              <span
                className="quiz-audio-card__seek-fill"
                style={{ width: `${progress}%` }}
              />
            </button>
            <div className="quiz-audio-card__controls">
              <button
                type="button"
                className="quiz-audio-card__ctrl"
                aria-label="Rewind 5 seconds"
                onClick={() => seekBy(-5)}
              >
                ‹‹
              </button>
              <button
                type="button"
                className="quiz-audio-card__ctrl quiz-audio-card__ctrl--play"
                aria-label={playing ? "Pause" : "Play"}
                onClick={togglePlay}
              >
                {playing ? "❚❚" : "▶"}
              </button>
              <button
                type="button"
                className="quiz-audio-card__ctrl"
                aria-label="Forward 5 seconds"
                onClick={() => seekBy(5)}
              >
                ››
              </button>
            </div>
          </>
        ) : question.question ? (
          <p className="quiz-audio-card__prompt" style={textStyle}>
            {question.question}
          </p>
        ) : (
          <p className="quiz-audio-card__prompt">No audio</p>
        )}
      </div>

      <div
        className="quiz-audio-card__options"
        role="radiogroup"
        aria-label={`Question ${index + 1}`}
      >
        {choices.map((choice) => {
          const id = `quiz-q${index}-${choice.key}`;
          const checked = selectedKey === choice.key;
          return (
            <label
              key={choice.key}
              htmlFor={id}
              className={`quiz-audio-card__option${
                checked ? " quiz-audio-card__option--selected" : ""
              }`}
            >
              <input
                id={id}
                type="radio"
                name={`quiz-audio-q-${index}`}
                value={choice.key}
                checked={checked}
                disabled={disabled}
                onChange={() => onSelect(choice.key)}
              />
              <span className="quiz-audio-card__radio" aria-hidden />
              <span className="quiz-audio-card__label">{choice.label}</span>
            </label>
          );
        })}
      </div>
    </article>
  );
}
